import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api, { formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, Truck, Wallet, AlertTriangle, PackageCheck, Route } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#0F4C3A", "#10B981", "#F59E0B", "#2563EB", "#E11D48", "#8B5CF6"];

function StatCard({ icon: Icon, label, value, accent, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
      <Card className="flex h-full items-start justify-between p-5">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight">{value}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </Card>
    </motion.div>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });

  if (isLoading || !data)
    return <div className="h-64 animate-pulse rounded-xl bg-secondary" data-testid="dashboard-loading" />;

  const stats = [
    { icon: PackageCheck, label: "Today's Deliveries", value: data.todays_deliveries, accent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
    { icon: PackageCheck, label: `Deliveries · ${data.month || "This Month"}`, value: data.total_deliveries, accent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
    { icon: Wallet, label: "Collected This Month", value: formatINR(data.total_collected), accent: "bg-primary/10 text-primary" },
    { icon: AlertTriangle, label: "Outstanding This Month", value: formatINR(data.total_outstanding), accent: "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" },
    { icon: Route, label: "Out for Delivery", value: data.active_deliveries, accent: "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300" },
    { icon: Users, label: "Customers", value: data.total_customers, accent: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" },
    { icon: Truck, label: "Delivery Staff", value: data.total_drivers, accent: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Current month overview — {data.month}. Figures reset on the 1st of every month.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((s, i) => (
          <StatCard key={s.label} {...s} i={i} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-display text-lg font-semibold">Area-wise Sales</h3>
          <p className="mb-4 text-xs text-muted-foreground">This month's business value by locality</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.area_breakdown}>
              <XAxis dataKey="area" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatINR(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]} fill="#0F4C3A" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-semibold">Payment Mode Split</h3>
          <p className="mb-4 text-xs text-muted-foreground">This month, by payment method</p>
          {data.payment_mode_split.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">No payments yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.payment_mode_split} dataKey="amount" nameKey="mode" cx="50%" cy="50%" outerRadius={90} label={(e) => e.mode}>
                  {data.payment_mode_split.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}
